import { apiFetch, isApiMode } from './http';

// ─────────────────────────────────────────────────────────────────────
// SUPPORT CORRECTS A PLAYER'S IDENTITY — /sa → «Spillerprofil» (#796).
//
// `GET /players` (platform staff since #1060) to find the player, and
// `PATCH /platform/players/:id` to correct name, profile address, phone and
// gender — with a written reason, behind `member.profile.support_edit`. The
// server derives what changed and writes a privileged audit row in the same
// transaction; this sends the fields and the reason, and nothing else.
// ─────────────────────────────────────────────────────────────────────

export interface SupportPlayer {
  /** The app's id: a NUMBER for seed players, a string for others. */
  id: string | number;
  name: string;
  email?: string;
  phone?: string;
  gender?: string;
}

export interface SupportEdit {
  name: string;
  email: string;
  phone: string;
  gender: string;
}

export type SupportPlayersResult =
  | { state: 'no-api' }
  | { state: 'ok'; players: SupportPlayer[] }
  | { state: 'refused'; code: string | null }
  | { state: 'error' };

export type SupportEditResult =
  | { state: 'ok'; player: SupportPlayer; changed: string[] }
  | { state: 'refused'; code: string }
  | { state: 'error'; status: number | null };

export async function loadSupportPlayers(): Promise<SupportPlayersResult> {
  if (!isApiMode()) return { state: 'no-api' };
  try {
    const res = await apiFetch('/players');
    if (res.ok) return { state: 'ok', players: (await res.json()) as SupportPlayer[] };
    if (res.status >= 500) return { state: 'error' };
    const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
    return { state: 'refused', code: typeof body?.error === 'string' ? body.error : null };
  } catch {
    return { state: 'error' };
  }
}

export async function supportEditPlayer(
  playerId: string | number,
  edit: SupportEdit,
  reason: string,
): Promise<SupportEditResult> {
  let res: Response;
  try {
    res = await apiFetch(`/platform/players/${encodeURIComponent(String(playerId))}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...edit, reason }),
    });
  } catch {
    return { state: 'error', status: null };
  }
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (res.ok && body?.player) {
    return {
      state: 'ok',
      player: body.player as SupportPlayer,
      changed: Array.isArray(body.changed) ? (body.changed as string[]) : [],
    };
  }
  if (res.status < 500 && typeof body?.error === 'string') {
    return { state: 'refused', code: body.error };
  }
  // A 5xx after the transaction cannot prove nothing changed (#944's lesson):
  // the caller says «reload and look», never «nothing was changed».
  return { state: 'error', status: res.status };
}
